import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../../api/client';
import { uploadAdminProductImage, ADMIN_CATALOG_TIMEOUT_MS } from '../../api/admin.api';
import DangerActionModal from '../../components/DangerActionModal';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import { ADMIN_PERMISSIONS } from '../../config/admin-permissions';
import { emptyCompositionRow, getApiErrorMessage, prepareImageForUpload } from './catalog/catalog.helpers';
import type { Category, CategoryDraft, Product, ProductDraft } from './catalog/catalog.types';
import { CategoriesList, ProductsList } from './catalog/CatalogLists';
import CatalogReorderSection from './catalog/CatalogReorderSection';
import CategoryForm from './catalog/CategoryForm';
import ProductForm from './catalog/ProductForm';
import './admin.css';

export default function AdminCatalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'products' | 'categories' | 'order'>('products');
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [editingCategory, setEditingCategory] = useState<CategoryDraft | null>(null);
  const [editingProduct, setEditingProduct] = useState<ProductDraft | null>(null);
  const [newCategoryNameForProduct, setNewCategoryNameForProduct] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [categoryProductSearch, setCategoryProductSearch] = useState('');
  const [showOnlyUncategorized, setShowOnlyUncategorized] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [dangerState, setDangerState] = useState<{ type: 'category' | 'product'; id: number; label: string } | null>(null);
  const [dangerLoading, setDangerLoading] = useState(false);
  const [dangerError, setDangerError] = useState<string | null>(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const [productSaving, setProductSaving] = useState(false);
  const [statusUpdatingIds, setStatusUpdatingIds] = useState<number[]>([]);
  const [compactView, setCompactView] = useState<boolean>(() => window.innerWidth <= 900);
  const { hasPermission } = useAdminPermissions();
  const canCreate = hasPermission(ADMIN_PERMISSIONS.CATALOG_CREATE);
  const canUpdate = hasPermission(ADMIN_PERMISSIONS.CATALOG_UPDATE);
  const canDelete = hasPermission(ADMIN_PERMISSIONS.CATALOG_DELETE);
  const canReorder = hasPermission(ADMIN_PERMISSIONS.CATALOG_REORDER);

  // Order tab: -1 = Все товары, 0 = Без категории, >0 = category id
  const [orderSelectedCategoryId, setOrderSelectedCategoryId] = useState<number | null>(null);
  const [orderCategoryRows, setOrderCategoryRows] = useState<Array<{ id: number; name: string; sort_order: number }>>([]);
  const [orderProductRows, setOrderProductRows] = useState<Array<{ id: number; name: string; sort_order: number; images: string[] }>>([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const [productsLimitLoaded, setProductsLimitLoaded] = useState(0);
  const ORDER_ALL = -1;
  const deepLinkTab = searchParams.get('tab');
  const deepLinkCategoryId = searchParams.get('categoryId');
  const deepLinkProductId = searchParams.get('productId');

  useEffect(() => {
    const onResize = () => setCompactView(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    void fetchCategories();
    void fetchProducts(100);
  }, []);
  useEffect(() => {
    if (activeTab === 'order' && productsLimitLoaded < 500) {
      void fetchProducts(500);
    }
  }, [activeTab, productsLimitLoaded]);
  useEffect(() => {
    if (deepLinkTab === 'categories' || deepLinkTab === 'products' || deepLinkTab === 'order') {
      setActiveTab(deepLinkTab);
    }
  }, [deepLinkTab]);
  useEffect(() => {
    const categoryId = Number(deepLinkCategoryId);
    if (!Number.isInteger(categoryId) || categoryId <= 0 || categories.length === 0) return;
    const found = categories.find((c) => c.id === categoryId);
    if (!found) return;
    setActiveTab('categories');
    setEditingCategory({
      ...found,
      product_ids: products.filter((p) => p.category_id === found.id).map((p) => p.id),
    });
    setCategoryProductSearch('');
    setShowOnlyUncategorized(false);
  }, [deepLinkCategoryId, categories, products]);
  useEffect(() => {
    const productId = Number(deepLinkProductId);
    if (!Number.isInteger(productId) || productId <= 0 || products.length === 0) return;
    const found = products.find((p) => p.id === productId);
    if (!found) return;
    setActiveTab('products');
    setEditingProduct(found);
  }, [deepLinkProductId, products]);

  // Sync order rows when data or selection changes. Initial order: sort_order, then id.
  useEffect(() => {
    if (activeTab === 'order') {
      setOrderCategoryRows(
        categories.map((c) => ({ id: c.id, name: c.name, sort_order: c.sort_order ?? 0 }))
      );
      if (orderSelectedCategoryId === null) {
        setOrderProductRows([]);
      } else {
        let list: Product[];
        if (orderSelectedCategoryId === ORDER_ALL) {
          list = [...products];
        } else if (orderSelectedCategoryId === 0) {
          list = products.filter((p) => p.category_id == null);
        } else {
          list = products.filter((p) => p.category_id === orderSelectedCategoryId);
        }
        const sorted = list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
        setOrderProductRows(
          sorted.map((p, i) => ({
            id: p.id,
            name: p.name,
            sort_order: i,
            images: p.images || [],
          }))
        );
      }
    }
  }, [activeTab, categories, products, orderSelectedCategoryId]);

  const fetchCategories = async () => {
    try {
      const res = await apiClient.get<{ success: true; data: Category[] }>('/api/admin/catalog/categories');
      setCategories(res.data?.data || []);
    } catch (e) {
      setError('Не удалось загрузить категории');
    }
  };

  const fetchProducts = async (limit = 100) => {
    setLoading(true);
    try {
      const res = await apiClient.get<{ success: true; data: { items: Product[] } }>(`/api/admin/catalog/products?limit=${limit}`);
      setProducts(res.data?.data?.items || []);
      setProductsLimitLoaded((prev) => Math.max(prev, limit));
    } catch (e) {
      setError('Не удалось загрузить товары');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (categorySaving) return;
    if (!editingCategory) return;
    if (!editingCategory.name?.trim()) {
      setError('Название категории обязательно');
      return;
    }
    try {
      setCategorySaving(true);
      const payload = {
        name: editingCategory.name.trim(),
        is_active: editingCategory.is_active ?? true,
        product_ids: editingCategory.product_ids || [],
        product_order_ids: editingCategory.product_ids || [],
      };
      if (editingCategory.id) {
        await apiClient.put(`/api/admin/catalog/categories/${editingCategory.id}`, payload);
      } else {
        await apiClient.post('/api/admin/catalog/categories', payload);
      }
      setEditingCategory(null);
      void fetchCategories();
      void fetchProducts(productsLimitLoaded >= 500 ? 500 : 100);
      setNotice('Категория сохранена. Действие зафиксировано в журнале.');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Ошибка при сохранении категории'));
    } finally {
      setCategorySaving(false);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    const category = categories.find((c) => c.id === id);
    setDangerError(null);
    setDangerState({ type: 'category', id, label: category?.name ?? `ID ${id}` });
  };
  const openCategoryEditor = (draft: CategoryDraft) => {
    setEditingCategory(draft);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'categories');
      if (draft.id) next.set('categoryId', String(draft.id));
      else next.delete('categoryId');
      next.delete('productId');
      return next;
    });
  };

  const handleSaveCategoryOrder = async () => {
    setOrderSaving(true);
    try {
      await apiClient.put('/api/admin/catalog/categories/reorder', {
        items: orderCategoryRows.map((r, i) => ({ id: r.id, sort_order: i })),
      });
      void fetchCategories();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Ошибка при сохранении порядка категорий'));
    } finally {
      setOrderSaving(false);
    }
  };

  const handleSaveProductOrder = async () => {
    if (orderSelectedCategoryId === null) return;
    setOrderSaving(true);
    try {
      await apiClient.put('/api/admin/catalog/products/reorder', {
        items: orderProductRows.map((r, i) => ({ id: r.id, sort_order: i })),
      });
      void fetchProducts(500);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Ошибка при сохранении порядка товаров'));
    } finally {
      setOrderSaving(false);
    }
  };

  const moveOrderRow = <T extends { id: number; sort_order: number }>(
    rows: T[],
    index: number,
    direction: 'up' | 'down'
  ): T[] => {
    const next = [...rows];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return rows;
    [next[index], next[swapIdx]] = [next[swapIdx], next[index]];
    return next.map((r, i) => ({ ...r, sort_order: i })) as T[];
  };

  const setOrderProductRowPosition = (rowId: number, newPos: number) => {
    const idx = orderProductRows.findIndex((r) => r.id === rowId);
    if (idx < 0) return;
    const pos = Math.max(0, Math.min(newPos, orderProductRows.length - 1));
    if (pos === idx) return;
    const next = [...orderProductRows];
    const [item] = next.splice(idx, 1);
    next.splice(pos, 0, item);
    setOrderProductRows(next.map((r, i) => ({ ...r, sort_order: i })));
  };

  const setOrderCategoryRowPosition = (rowId: number, newPos: number) => {
    const idx = orderCategoryRows.findIndex((r) => r.id === rowId);
    if (idx < 0) return;
    const pos = Math.max(0, Math.min(newPos, orderCategoryRows.length - 1));
    if (pos === idx) return;
    const next = [...orderCategoryRows];
    const [item] = next.splice(idx, 1);
    next.splice(pos, 0, item);
    setOrderCategoryRows(next.map((r, i) => ({ ...r, sort_order: i })));
  };

  const resetOrderDrafts = () => {
    setOrderCategoryRows(categories.map((c) => ({ id: c.id, name: c.name, sort_order: c.sort_order ?? 0 })));
    if (orderSelectedCategoryId === null) {
      setOrderProductRows([]);
      return;
    }
    let list: Product[];
    if (orderSelectedCategoryId === ORDER_ALL) list = [...products];
    else if (orderSelectedCategoryId === 0) list = products.filter((p) => p.category_id == null);
    else list = products.filter((p) => p.category_id === orderSelectedCategoryId);
    const sorted = list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
    setOrderProductRows(sorted.map((p, i) => ({ id: p.id, name: p.name, sort_order: i, images: p.images || [] })));
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (productSaving) return;
    if (!editingProduct) return;
    if (!editingProduct.name?.trim()) {
      setError('Название товара обязательно');
      return;
    }
    if (editingProduct.price == null || Number.isNaN(Number(editingProduct.price))) {
      setError('Цена обязательна');
      return;
    }
    if (!editingProduct.images || editingProduct.images.length === 0) {
      setError('Добавьте хотя бы одно изображение товара');
      return;
    }
    try {
      setProductSaving(true);
      let categoryId: number | null | undefined =
        editingProduct.category_id === 'all' ? null : typeof editingProduct.category_id === 'number' ? editingProduct.category_id : null;
      if (editingProduct.category_id === '__new__') {
        if (!newCategoryNameForProduct.trim()) {
          setError('Введите название новой категории');
          return;
        }
        const categoryRes = await apiClient.post<{ success: true; data: Category }>(
          '/api/admin/catalog/categories',
          { name: newCategoryNameForProduct.trim(), is_active: true },
          { timeout: ADMIN_CATALOG_TIMEOUT_MS }
        );
        categoryId = categoryRes.data?.data?.id;
        await fetchCategories();
      }

      const payload = {
        ...editingProduct,
        category_id: categoryId ?? null,
        cost_price:
          editingProduct.cost_price == null || Number.isNaN(Number(editingProduct.cost_price))
            ? null
            : Number(editingProduct.cost_price),
      };
      if (editingProduct.id) {
        await apiClient.put(`/api/admin/catalog/products/${editingProduct.id}`, payload, {
          timeout: ADMIN_CATALOG_TIMEOUT_MS,
        });
      } else {
        await apiClient.post('/api/admin/catalog/products', payload, {
          timeout: ADMIN_CATALOG_TIMEOUT_MS,
        });
      }
      setEditingProduct(null);
      setNewCategoryNameForProduct('');
      void fetchProducts(productsLimitLoaded >= 500 ? 500 : 100);
      setNotice('Товар сохранен. Действие зафиксировано в журнале.');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Ошибка при сохранении товара'));
    } finally {
      setProductSaving(false);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    const product = products.find((p) => p.id === id);
    setDangerError(null);
    setDangerState({ type: 'product', id, label: product?.name ?? `ID ${id}` });
  };
  const openProductEditor = (draft: ProductDraft) => {
    setEditingProduct(draft);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'products');
      if (draft.id) next.set('productId', String(draft.id));
      else next.delete('productId');
      next.delete('categoryId');
      return next;
    });
  };

  const patchProductStatus = async (
    product: Product,
    patch: Partial<Pick<Product, 'is_active' | 'in_stock'>>
  ) => {
    if (statusUpdatingIds.includes(product.id)) return;
    const previous = { is_active: product.is_active, in_stock: product.in_stock };
    setStatusUpdatingIds((prev) => [...prev, product.id]);
    setProducts((prev) => prev.map((item) => (item.id === product.id ? { ...item, ...patch } : item)));
    try {
      const res = await apiClient.patch<{ success: true; data: Product }>(
        `/api/admin/catalog/products/${product.id}/status`,
        patch,
        { timeout: ADMIN_CATALOG_TIMEOUT_MS }
      );
      const nextData = res.data?.data;
      if (nextData) {
        setProducts((prev) => prev.map((item) => (item.id === product.id ? { ...item, ...nextData } : item)));
      }
      setNotice('Статус товара обновлен.');
    } catch (e) {
      setProducts((prev) => prev.map((item) => (item.id === product.id ? { ...item, ...previous } : item)));
      setError(getApiErrorMessage(e, 'Не удалось обновить статус товара'));
    } finally {
      setStatusUpdatingIds((prev) => prev.filter((id) => id !== product.id));
    }
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !editingProduct) return;
    try {
      setUploadingImage(true);
      const uploadedUrls: string[] = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const prepared = await prepareImageForUpload(file);
        const url = await uploadAdminProductImage({
          fileName: file.name,
          contentType: prepared.contentType,
          dataBase64: prepared.dataBase64,
        });
        uploadedUrls.push(url);
      }
      if (uploadedUrls.length === 0) {
        setError('Выберите файлы изображений');
        return;
      }
      setEditingProduct({
        ...editingProduct,
        images: [...(editingProduct.images || []), ...uploadedUrls],
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить изображение'));
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const composition = editingProduct?.composition || [emptyCompositionRow];

  return (
    <div className="admin-page">
      <div className="admin-tabs">
        <button 
          className={`admin-btn ${activeTab === 'products' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          data-testid="admin-catalog-tab-products"
          onClick={() => {
            setActiveTab('products');
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', 'products');
              return next;
            });
          }}
        >
          Товары
        </button>
        <button 
          className={`admin-btn ${activeTab === 'categories' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          data-testid="admin-catalog-tab-categories"
          onClick={() => {
            setActiveTab('categories');
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', 'categories');
              return next;
            });
          }}
        >
          Категории
        </button>
        <button 
          className={`admin-btn ${activeTab === 'order' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
          data-testid="admin-catalog-tab-order"
          onClick={() => {
            setActiveTab('order');
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', 'order');
              return next;
            });
          }}
        >
          Порядок
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="admin-card">{notice}</div>}
      {!canUpdate && <div className="admin-card">У вас только режим просмотра каталога: действия изменения скрыты или заблокированы.</div>}

      {activeTab === 'categories' && (
        <div>
          <div className="admin-section-header">
            <h2 className="admin-card-title">Категории</h2>
            <button
              className="admin-btn admin-btn-primary"
              disabled={!canCreate}
              onClick={() => {
                setEditingCategory({ is_active: true, product_ids: [] });
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('tab', 'categories');
                  next.delete('categoryId');
                  next.delete('productId');
                  return next;
                });
                setCategoryProductSearch('');
                setShowOnlyUncategorized(false);
              }}
            >
              + Добавить
            </button>
          </div>

          {editingCategory && (
            <CategoryForm
              editingCategory={editingCategory}
              products={products}
              categoryProductSearch={categoryProductSearch}
              showOnlyUncategorized={showOnlyUncategorized}
              categorySaving={categorySaving}
              canCreate={canCreate}
              canUpdate={canUpdate}
              onSubmit={handleSaveCategory}
              setEditingCategory={setEditingCategory}
              setCategoryProductSearch={setCategoryProductSearch}
              setShowOnlyUncategorized={setShowOnlyUncategorized}
              onCancel={() => {
                setEditingCategory(null);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('categoryId');
                  return next;
                });
              }}
            />
          )}

          <CategoriesList
            categories={categories}
            products={products}
            compactView={compactView}
            canDelete={canDelete}
            onDelete={handleDeleteCategory}
            onEdit={(category) => {
              openCategoryEditor(category);
              setCategoryProductSearch('');
              setShowOnlyUncategorized(false);
            }}
          />
        </div>
      )}

      {activeTab === 'order' && (
        <CatalogReorderSection
          orderCategoryRows={orderCategoryRows}
          orderProductRows={orderProductRows}
          orderSelectedCategoryId={orderSelectedCategoryId}
          categories={categories}
          orderSaving={orderSaving}
          canReorder={canReorder}
          orderAllValue={ORDER_ALL}
          onSelectCategory={setOrderSelectedCategoryId}
          onSetCategoryPos={setOrderCategoryRowPosition}
          onSetProductPos={setOrderProductRowPosition}
          onMoveCategory={(idx, direction) => setOrderCategoryRows((prev) => moveOrderRow(prev, idx, direction))}
          onMoveProduct={(idx, direction) => setOrderProductRows((prev) => moveOrderRow(prev, idx, direction))}
          onSaveCategoryOrder={handleSaveCategoryOrder}
          onSaveProductOrder={handleSaveProductOrder}
          onResetDrafts={resetOrderDrafts}
        />
      )}

      {activeTab === 'products' && (
        <div>
          <div className="admin-section-header">
            <h2 className="admin-card-title">Товары</h2>
            <button
              className="admin-btn admin-btn-primary"
              disabled={!canCreate}
              onClick={() => {
                openProductEditor({ is_active: true, in_stock: true, is_hit: false, images: [], composition: [{ ...emptyCompositionRow }], stock_quantity: undefined });
                setNewCategoryNameForProduct('');
              }}
            >
              + Добавить
            </button>
          </div>

          {editingProduct && (
            <ProductForm
              editingProduct={editingProduct}
              categories={categories}
              composition={composition}
              emptyCompositionRow={emptyCompositionRow}
              newCategoryNameForProduct={newCategoryNameForProduct}
              uploadingImage={uploadingImage}
              productSaving={productSaving}
              canCreate={canCreate}
              canUpdate={canUpdate}
              onSubmit={handleSaveProduct}
              onUploadImage={handleUploadImage}
              setEditingProduct={setEditingProduct}
              setNewCategoryNameForProduct={setNewCategoryNameForProduct}
              onCancel={() => {
                setEditingProduct(null);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('productId');
                  return next;
                });
              }}
            />
          )}

          <ProductsList
            products={products}
            compactView={compactView}
            loading={loading}
            canUpdate={canUpdate}
            canDelete={canDelete}
            isStatusUpdating={(productId) => statusUpdatingIds.includes(productId)}
            onToggleActive={(product, nextValue) => {
              void patchProductStatus(product, { is_active: nextValue });
            }}
            onToggleInStock={(product, nextValue) => {
              void patchProductStatus(product, { in_stock: nextValue });
            }}
            onEdit={(product) => openProductEditor(product)}
            onDelete={handleDeleteProduct}
          />
        </div>
      )}
      <DangerActionModal
        open={dangerState != null}
        title="Опасное действие"
        description="Удаление необратимо и может повлиять на витрину и отчеты."
        entityLabel={dangerState?.label ?? ''}
        confirmationText="DELETE"
        requireReason
        loading={dangerLoading}
        error={dangerError}
        onClose={() => {
          if (!dangerLoading) setDangerState(null);
        }}
        onConfirm={async ({ reason }) => {
          if (!dangerState) return;
          setDangerLoading(true);
          setDangerError(null);
          try {
            if (dangerState.type === 'category') {
              await apiClient.delete(`/api/admin/catalog/categories/${dangerState.id}`, { data: { reason } });
              void fetchCategories();
            } else {
              await apiClient.delete(`/api/admin/catalog/products/${dangerState.id}`, { data: { reason } });
              void fetchProducts(productsLimitLoaded >= 500 ? 500 : 100);
            }
            setNotice('Действие успешно выполнено.');
            setDangerState(null);
          } catch (e) {
            setDangerError(getApiErrorMessage(e, 'Ошибка при удалении'));
          } finally {
            setDangerLoading(false);
          }
        }}
      />
    </div>
  );
}
